

import { TimelineMarker } from './types/insights';
import { Belt } from './types';
import { TimelineTickMark } from './TimelineTickMark';
import { useIsMobile } from '@/hooks/use-mobile';

interface TimelineTickElementsProps {
  markerGroups: Record<string, TimelineMarker[]>;
  belts: Belt[];
  baseBeltSpacing: number;
  stripeSpacings: number[];
  onMarkerClick: (marker: TimelineMarker) => void;
}

export function TimelineTickElements({
  markerGroups,
  belts,
  baseBeltSpacing,
  stripeSpacings,
  onMarkerClick
}: TimelineTickElementsProps) {
  const START_X = 40;
  const isMobile = useIsMobile();
  const TICK_SPACING = isMobile ? 20 : 10; 
  const tickElements: JSX.Element[] = [];

  // Check if markerGroups is valid
  if (!markerGroups || typeof markerGroups !== 'object') {
    console.error('Invalid markerGroups:', markerGroups);
    return <div className="absolute inset-0 pointer-events-none"></div>;
  }

  try {
    // Generate tick marks for each group
    for (const key in markerGroups) {
      const [beltRank, stripeRaw] = key.split('_');
      const stripeCount = parseInt(stripeRaw || '0', 10);
      const beltIndex = belts.findIndex(b => b.dbColor.toLowerCase() === beltRank);
      if (beltIndex === -1) continue;

      const group = markerGroups[key];
      if (!Array.isArray(group)) continue;
      
      // Sort the markers by date in ascending order (oldest to newest)
      // This ensures that the newest markers are rendered further to the right
      const sortedGroup = [...group].sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date.getTime() - b.date.getTime();
      });
      
      const groupIndex = beltIndex * 5 + stripeCount;

      let segmentStart = START_X;
      for (let i = 0; i < groupIndex; i++) {
        segmentStart += stripeSpacings[i] || baseBeltSpacing;
      }

      const offset = stripeCount === 0 ? 40 : 10;
      segmentStart += offset;

      sortedGroup.forEach((marker, i) => {
        if (!marker || !marker.type) return;
        
        const x = segmentStart + TICK_SPACING * i;
        tickElements.push(
          <TimelineTickMark
            key={`${marker.type}-${i}-${x}`}
            marker={marker}
            x={x}
            onClick={onMarkerClick}
          />
        );
      });
    }
  } catch (err) {
    console.error('Error rendering tick elements:', err);
  }

  return (
    <div className="absolute inset-0 pointer-events-none">
      {tickElements}
    </div>
  );
}
